import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  StationFormData,
  StationFile,
  CreateStationDialog,
  ViewStationDialog,
  ViewFileDialog,
  UploadFileDialog,
  StationsHeader,
  TabType,
  ReorderStationsDialog,
} from "./components";

import StationDetailsPanel from "./components/StationDetailsPanel/StationDetailsPanel";

import { getAllStationsWithVersions, createStation, createStationVersion, deleteStation, getStationById, deleteStationVersion, forceDeleteStation, toggleStationVersion, type Station, type StationWithChildren } from "./api";
import { DataTable } from "lift-ui";
import type { TableColumn } from "lift-ui";
import { Button, Badge, Tooltip, TooltipTrigger, TooltipContent, TooltipProvider, SpinnerLoader, Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "lift-ui";
import { Loader2, Eye, Trash2, Power } from "lucide-react";
import { sortByLatestDate } from "../../../shared/utils/sortUtils";

const initialFormData: StationFormData = {
  name: "",
  system_prompt: "",
  default_model: "gemini-2.5-flash", // Default model for API
  allowed_tools: ["web_search"], // Default tools for API
  max_tokens: 4000, // Default token limit for API
  temperature: 0.7, // Default temperature for API
  is_active: true, // Default to active for new stations
  policies: { tone: 'professional', focus: 'helpful', specialties: [] }, // Default policies for API
  version_note: {},
  meta_config: {
    avatar: '',
    iconSrc: '',
    category: '',
    color: '', // Default uses theme primary
    badgeColor: 'bg-blue-100', // Default badge color (Tailwind class)
    iconBgColor: 'bg-gray-100', // Icon background color (Tailwind class)
    textColor: 'text-gray-900', // Text color (Tailwind class)
    description_brief: '',
    description_main: '',
    base_prompts: [
      { id: `starter-1`, text: '' },
      { id: `starter-2`, text: '' }
    ], // Starters array - default with 2 empty starters
    custom_gpt: '', // Custom GPT link field
    order: undefined, // Order field - undefined means no order set
  },
  parent_buddy_id: '',
  avatarFile: undefined, // Avatar file for upload
};

// Stations will be fetched from backend – keeping empty array as initial value
const dummyStations: Station[] = [];

const removeStationById = (arr: StationWithChildren[], id: string): StationWithChildren[] =>
  arr.reduce<StationWithChildren[]>((acc, station) => {
    const stationId = station.buddy_id;

    if (stationId === id) {
      return acc; // Skip this station
    }

    const stationCopy = { ...station } as StationWithChildren;
    if (stationCopy.children && stationCopy.children.length > 0) {
      stationCopy.children = removeStationById(stationCopy.children, id);
    }

    acc.push(stationCopy);
    return acc;
  }, []);

interface StationsProps {
  hideHeader?: boolean;
  onHandlersReady?: (handlers: {
    onCreateStation: () => void;
    onUploadFiles: () => void;
    onReorder: () => void;
    searchTerm: string;
    onSearch: (term: string) => void;
  }) => void;
  hasEditAccess?: boolean;
  hasManageAccess?: boolean;
}

const Stations: React.FC<StationsProps> = ({ hideHeader = false, onHandlersReady, hasEditAccess = true, hasManageAccess = true }) => {
  const [stations, setStations] = useState<Station[]>(dummyStations);
  const stationsRef = useRef<Station[]>(dummyStations);
  const [filteredStations, setFilteredStations] = useState<Station[]>(dummyStations);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState<TabType>("stations");
  const [selectedStation, setSelectedStation] = useState<Station | null>(null);
  const [detailStation, setDetailStation] = useState<Station | null>(null);

  // Files state
  const [stationFiles, setStationFiles] = useState<StationFile[]>([]);
  const [selectedFile, setSelectedFile] = useState<StationFile | null>(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);

  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewFileDialogOpen, setViewFileDialogOpen] = useState(false);
  const [uploadFileDialogOpen, setUploadFileDialogOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [orderDialogOpen, setOrderDialogOpen] = useState(false);
  const [stationToDelete, setStationToDelete] = useState<Station | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Form data for edit/create
  const [formData, setFormData] = useState<StationFormData>(initialFormData);
  const [originalFormData, setOriginalFormData] = useState<StationFormData>(initialFormData);
  const [isVersionCreation, setIsVersionCreation] = useState(false);
  

  const forceUI = () => {
    setDetailStation(prev => prev ? ({ ...prev }) : prev);
  };

  const handleInputChange = (field: keyof StationFormData, value: any) => {
    console.log(`🔄 handleInputChange called with field: ${field}`, value);
    // Use functional update to ensure we always work with the latest state
    setFormData((prevFormData) => {
      const updated = { ...prevFormData, [field]: value };
      console.log('📝 Updated formData:', updated);
      return updated;
    });
  };

  const resetDialog = () => {
    setFormData(initialFormData);
    setOriginalFormData(initialFormData);
    setIsVersionCreation(false);
    setSelectedStation(null);
  };

  useEffect(() => {
    fetchStations();
  }, []);

  useEffect(() => {
    filterStations();
  }, [stations, searchTerm]);

  const fetchStations = async () => {
    setLoading(true);
    try {
      const data = await getAllStationsWithVersions();
      const sortedData = sortByLatestDate(data);
      stationsRef.current = sortedData;
      setStations(sortedData);
      return sortedData;
    } catch (error) {
      console.error("Error fetching stations:", error);
      stationsRef.current = [];
      setStations([]);
      return [] as Station[];
    } finally {
      setLoading(false);
    }
  };

  const filterStations = () => {
    if (!searchTerm.trim()) {
      setFilteredStations(stations);
      return;
    }

    const filtered = stations.filter((station) => {
      const matchesName = station.name.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesModel = station.default_model?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesId = station.buddy_id.toLowerCase().includes(searchTerm.toLowerCase());

      return matchesName || matchesModel || matchesId;
    });

    const sortedFiltered = sortByLatestDate(filtered);
    setFilteredStations(sortedFiltered);
  };

  // Pagination logic
  const getActiveVersion = (station: StationWithChildren): Station => {
    if (station.is_active) return station;
    if (station.children?.length) {
      const activeChild = station.children.find((c: Station) => c.is_active);
      return activeChild || station;
    }
    return station;
  };

  const activeStationsOnly = filteredStations.map((s) => getActiveVersion(s as StationWithChildren));

  const getPaginatedStations = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return activeStationsOnly.slice(startIndex, endIndex);
  };

  const totalPages = Math.ceil(activeStationsOnly.length / itemsPerPage);

  // Station CRUD operations
  const handleCreateStation = async () => {
    try {
      console.log('🚀 handleCreateStation called with formData:', formData);
      console.log('📁 Avatar file in formData:', formData.avatarFile);

      if (isVersionCreation) {
        // Ensure parent_buddy_id is provided for version creation
        if (!formData.parent_buddy_id) {
          console.error("Parent buddy ID is required for version creation");

          return;
        }
        
        // Find the currently active version before creating the new one
        const parentStation = stations.find(s => s.buddy_id === formData.parent_buddy_id) as StationWithChildren | undefined;
        let previousActiveVersionId: string | null = null;
        
        if (parentStation) {
          if (parentStation.is_active) {
            previousActiveVersionId = parentStation.buddy_id;
          } else if (parentStation.children?.length) {
            const activeChild = parentStation.children.find((c: Station) => c.is_active);
            if (activeChild) {
              previousActiveVersionId = activeChild.buddy_id;
            }
          }
        }
        
        const { parent_buddy_id, ...versionData } = formData;
        await createStationVersion({
          ...versionData,
          parent_agent_id: parent_buddy_id, // API expects parent_agent_id
        });
        
        // Deactivate the previously active version so only the new one is active
        if (previousActiveVersionId) {
          await toggleStationVersion(previousActiveVersionId, false);
        }
      } else {
        await createStation(formData);
      }

      // Refresh the stations list and derive updated nodes locally (avoid extra buddy call)
      const updatedList = await fetchStations();

      // If a station is currently selected and we created a version for it,
      // update the selected/detail station from the refreshed list
      if (selectedStation && isVersionCreation && formData.parent_buddy_id === selectedStation.buddy_id) {
        const updatedRoot = updatedList.find(s => s.buddy_id === selectedStation.buddy_id);
        if (updatedRoot) setSelectedStation(updatedRoot);
      }

      if (detailStation && isVersionCreation && formData.parent_buddy_id === detailStation.buddy_id) {
        const updatedRoot = updatedList.find(s => s.buddy_id === detailStation.buddy_id);
        if (updatedRoot) setDetailStation(updatedRoot);
      }

      // Close the dialog and reset state
      setCreateDialogOpen(false);
      setIsVersionCreation(false);
      setFormData(initialFormData);
      setOriginalFormData(initialFormData);
    } catch (error) {
      console.error("Error creating station:", error);
    }
  };

  const handleDeleteStation = (station: Station) => {
    setStationToDelete(station);
    setConfirmDialogOpen(true);
  };

  const confirmDeleteStation = async () => {
    if (!stationToDelete) return;

    const buddyId = stationToDelete.buddy_id;
    const isParent = !(stationToDelete as any).parent_buddy_id;

    setDeleteLoading(true);
    try {
      if (isParent) {
        await forceDeleteStation(buddyId);
      } else {
        await deleteStation(buddyId);
      }

      // Call all-buddies API and reload the page
      const updatedStations = await fetchStations();

      // If we're viewing this station in detail panel and it's a parent, go back to main stations page
      if (detailStation && isParent && detailStation.buddy_id === buddyId) {
        setDetailStation(null);
      }

      // If we deleted a child version and are in detail view, refresh the detail station
      if (detailStation && !isParent) {
        const updatedRoot = updatedStations.find(s => s.buddy_id === detailStation.buddy_id);
        if (updatedRoot) {
          setDetailStation(updatedRoot);
        }
      }

      setConfirmDialogOpen(false);
      setStationToDelete(null);
    } catch (error) {
      console.error("Error deleting station:", error);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleViewStation = (station: Station) => {
    setSelectedStation(station);
    setViewDialogOpen(true);
  };

  const openCreateVersion = (parent: Station, activeVersion?: Station) => {
    const versionToCopy = activeVersion || parent;
    const prepopulatedData = {
      name: versionToCopy.name,
      system_prompt: versionToCopy.system_prompt || '',
      default_model: versionToCopy.default_model || 'gemini-2.5-flash',
      allowed_tools: (versionToCopy.available_tools) || [],
      max_tokens: versionToCopy.max_tokens || 4000,
      temperature: typeof versionToCopy.temperature === 'number' ? versionToCopy.temperature : 0.7,
      is_active: versionToCopy.is_active,
      policies: versionToCopy.policies || { tone: '', focus: '', specialties: [] },
      version_note: { description: '' }, // Clear version notes for new version
      meta_config: versionToCopy.meta_config ? {
        ...versionToCopy.meta_config,
        order: versionToCopy.meta_config.order !== undefined ? Number(versionToCopy.meta_config.order) : undefined,
      } : {
        avatar: '',
        iconSrc: '',
        category: '',
        color: '',
        description_brief: '',
        description_main: '',
        order: undefined,
      },
      parent_buddy_id: parent.buddy_id,
    };

    setFormData(prepopulatedData);
    setOriginalFormData(prepopulatedData);
    setIsVersionCreation(true);
    setCreateDialogOpen(true);
  };

  const handleStationSelect = (station: Station) => {
    const id = station.buddy_id;
    const currentStations = stationsRef.current as StationWithChildren[];
    const findRoot = (list: StationWithChildren[]): StationWithChildren | null => {
      for (const root of list) {
        const rootId = root.buddy_id;
        if (rootId === id) return root;
        if (root.children?.length) {
          if (root.children.some((c: StationWithChildren) => c.buddy_id === id)) return root;
        }
      }
      return station as StationWithChildren;
    };
    const rootStation = findRoot(currentStations);
    setDetailStation(rootStation as Station);
  };

  const handleFileView = (file: StationFile) => {
    setSelectedFile(file);
    setViewFileDialogOpen(true);
  };

  const handleUploadFiles = () => {
    if (!selectedStation) return;
    setUploadFileDialogOpen(true);
  };

  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    if (tab === "files" && selectedStation) {
      // Load files for selected station if needed
    }
  };

  const handleSearch = (term: string) => {
    setSearchTerm(term);
    setCurrentPage(1); // Reset to first page on search
  };

  // Expose handlers to parent
  useEffect(() => {
    if (onHandlersReady) {
      onHandlersReady({
        onCreateStation: () => {
          setFormData(initialFormData);
          setOriginalFormData(initialFormData);
          setIsVersionCreation(false);
          setCreateDialogOpen(true);
        },
        onUploadFiles: handleUploadFiles,
        onReorder: () => {
          setOrderDialogOpen(true);
        },
        searchTerm,
        onSearch: handleSearch,
      });
    }
  }, [onHandlersReady, searchTerm, selectedStation]);

  const stationColumns: TableColumn<Station>[] = useMemo(
    () => [
      {
        key: "name",
        header: "NAME",
        accessor: "name",
        render: (_value, row) => <span className="font-medium">{row.name}</span>,
      },
      {
        key: "version",
        header: "VERSION",
        accessor: (row) => row.version,
        render: (_value, row) => {
          const isChildRow = !!(row as any).parent_buddy_id;
          const versionLabel = isChildRow ? `v${row.version}` : `v${row.version} (Parent)`;
          return (
            <span className="inline-block px-1.5 py-0.5 text-xs rounded border border-gray-200 bg-gray-50 text-gray-700 font-mono">
              {versionLabel}
            </span>
          );
        },
      },
      {
        key: "created_at",
        header: "CREATED",
        accessor: "created_at",
        render: (_value, row) =>
          row.created_at ? new Date(row.created_at).toLocaleDateString() : "Unknown",
      },
      {
        key: "sort_order",
        header: "ORDER",
        accessor: "sort_order",
        align: "center",
        render: (_value, row) => (
          <span className="inline-block px-2 py-1 text-xs font-semibold rounded-md bg-gray-100 text-gray-700 border border-gray-200 min-w-[32px] text-center">
            {row.sort_order !== undefined ? row.sort_order : "-"}
          </span>
        ),
      },
      {
        key: "status",
        header: "STATUS",
        accessor: (row) => (row.is_active ? "Active" : "Inactive"),
        render: (_value, row) => (
          <Badge
            variant="outline"
            className={`text-xs font-medium ${
              row.is_active
                ? "bg-green-100 text-green-800 border-green-300"
                : "bg-gray-100 text-gray-600 border-gray-300"
            }`}
          >
            {row.is_active ? "Active" : "Inactive"}
          </Badge>
        ),
      },
      {
        key: "actions",
        header: "ACTIONS",
        accessor: (row) => row.buddy_id,
        align: "center",
        render: (_value, row) => (
          <div className="flex items-center justify-center gap-1">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleStationSelect(row)}
                    className="h-8 w-8 p-0"
                  >
                    <Eye className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>View Details</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            {hasEditAccess && !row.is_active && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={async () => {
                        try {
                          await toggleStationVersion(row.buddy_id, true);
                          await fetchStations();
                        } catch (e) {
                          console.error(e);
                        }
                      }}
                      className="h-8 w-8 p-0"
                    >
                      <Power className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Activate Version</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            {hasManageAccess && (() => {
              const isParent = !(row as any).parent_buddy_id;
              return (
                <TooltipProvider key="delete">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteStation(row)}
                        className="h-8 w-8 p-0 text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{isParent ? "Delete Station" : "Delete Version"}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              );
            })()}
          </div>
        ),
      },
    ],
    [hasEditAccess, hasManageAccess]
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-gray-600" />
      </div>
    );
  }

  return (
    <div>
      {!detailStation && !hideHeader && (
        <StationsHeader
          searchTerm={searchTerm}
          onSearch={handleSearch}
          activeTab={activeTab}
          onTabChange={handleTabChange}
          onCreateStation={() => {
            setFormData(initialFormData);
            setOriginalFormData(initialFormData);
            setIsVersionCreation(false);
            setCreateDialogOpen(true);
          }}
          onUploadFiles={handleUploadFiles}
          canUploadFiles={!!selectedStation}
          stations={stations}
          onReorderComplete={fetchStations}
        />
      )}

      {!detailStation && (
        <div className="flex-1 flex flex-col min-h-0">
          <DataTable<Station>
            className="flex-1 min-h-0 overflow-auto"
            maxHeight="calc(100vh - 200px)"
            columns={stationColumns}
            data={getPaginatedStations()}
            loading={loading}
            emptyMessage="No stations available."
            stickyHeader
            stickyFooter
            pagination={{
              currentPage,
              totalPages,
              pageSize: itemsPerPage,
              totalItems: activeStationsOnly.length,
              onPageChange: setCurrentPage,
              onPageSizeChange: setItemsPerPage,
              pageSizeOptions: [10, 25, 50, 100],
            }}
            showActions={false}
          />
        </div>
      )}

      {detailStation && (
        <div className="flex-1 flex flex-col min-h-0">
          <StationDetailsPanel
            station={detailStation}
            onViewStation={handleViewStation}
            onDeleteStation={handleDeleteStation}
            onCreateVersion={openCreateVersion}
            onStationsUpdated={async () => {
              const data = await fetchStations();
              // Also update the detail station if it's the same as the one being modified
              if (detailStation) {
                const updatedRoot = data.find(s => s.buddy_id === detailStation.buddy_id);
                if (updatedRoot) setDetailStation(updatedRoot);
              }
            }}
            onBack={() => setDetailStation(null)}
            hasEditAccess={hasEditAccess}
            hasManageAccess={hasManageAccess}
          />
        </div>
      )}

      {/* Dialogs */}
      <CreateStationDialog
        open={createDialogOpen}
        onClose={() => {
          setCreateDialogOpen(false);
          resetDialog();
        }}
        formData={formData}
        onChange={handleInputChange}
        onSubmit={handleCreateStation}
        isVersionCreation={isVersionCreation}
      />

      <ViewStationDialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        station={selectedStation}
      />

      <ViewFileDialog
        open={viewFileDialogOpen}
        onClose={() => {
          setViewFileDialogOpen(false);
          setSelectedFile(null);
        }}
        file={selectedFile}
      />

      <UploadFileDialog
        open={uploadFileDialogOpen}
        onClose={() => setUploadFileDialogOpen(false)}
        stationId={selectedStation?.buddy_id || ''}
        onFilesUploaded={fetchStations}
      />

      <Dialog open={confirmDialogOpen} onOpenChange={(open) => {
        if (!open) {
          setConfirmDialogOpen(false);
          setStationToDelete(null);
        }
      }}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Delete Station</DialogTitle>
          </DialogHeader>
          <div>
            <p className="text-sm text-gray-600">
              Are you sure you want to delete &quot;{stationToDelete?.name}&quot;? This action cannot be undone.
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setConfirmDialogOpen(false);
                setStationToDelete(null);
              }}
              disabled={deleteLoading}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDeleteStation}
              disabled={deleteLoading}
              className="flex items-center gap-2"
            >
              {deleteLoading && <SpinnerLoader size={16} />}
              {deleteLoading ? "Processing..." : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ReorderStationsDialog
        open={orderDialogOpen}
        onClose={() => setOrderDialogOpen(false)}
        stations={stations}
        onReorderComplete={fetchStations}
      />
    </div>
  );
};

export default Stations;